// src/extenders/video-player.js
var VideoPlayer = class extends HTMLElement {
  constructor() {
    super();
    this.video = null;
    this.playButton = null;
    this.playIcon = null;
    this.pauseIcon = null;
    this.boundTogglePlay = this.togglePlay.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this);
    this.boundShowPlayButton = this.showPlayButton.bind(this);
    this.boundShowPauseButton = this.showPauseButton.bind(this);
  }
  /**
   * Lifecycle callback invoked when the element is connected to the DOM.
   * Initializes event listeners and establishes component functionality.
   *
   * Sets up:
   * - Video and button element references
   * - Icon element references for state management
   * - Click handler for play/pause toggling
   * - Video state change listeners
   * - Keyboard accessibility support
   */
  connectedCallback() {
    this.video = this.querySelector("video");
    this.playButton = this.querySelector("[data-video-play-button]");
    this.playIcon = this.querySelector(".play-icon");
    this.pauseIcon = this.querySelector(".pause-icon");
    if (this.video && this.playButton) {
      this.playButton.addEventListener("click", this.boundTogglePlay);
      this.playButton.addEventListener("keydown", this.boundHandleKeydown);
      this.video.addEventListener("pause", this.boundShowPlayButton);
      this.video.addEventListener("play", this.boundShowPauseButton);
    }
  }
  /**
   * Lifecycle callback invoked when the element is disconnected from the DOM.
   * Removes event listeners to prevent memory leaks.
   */
  disconnectedCallback() {
    if (this.playButton) {
      this.playButton.removeEventListener("click", this.boundTogglePlay);
      this.playButton.removeEventListener("keydown", this.boundHandleKeydown);
    }
    if (this.video) {
      this.video.removeEventListener("pause", this.boundShowPlayButton);
      this.video.removeEventListener("play", this.boundShowPauseButton);
    }
  }
  /**
   * Handles keyboard interactions for accessibility.
   * Supports Enter and Space keys for play/pause functionality.
   *
   * @param {KeyboardEvent} event - Keyboard event object
   * @private
   */
  handleKeydown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this.togglePlay();
    }
  }
  /**
   * Manages the playing state of the video player through CSS class manipulation.
   *
   * Uses a single 'playing' class to represent the active state, following
   * the principle of minimal state management. This approach provides better
   * performance and cleaner CSS compared to multiple class toggles.
   *
   * @param {boolean} isPlaying - Boolean indicating whether the video should be in playing state
   */
  togglePlayingState(isPlaying) {
    if (!this.playButton)
      return;
    if (isPlaying) {
      this.playButton.classList.add("playing");
      this.playButton.setAttribute("aria-pressed", "true");
      this.toggleIcons(true);
    } else {
      this.playButton.classList.remove("playing");
      this.playButton.setAttribute("aria-pressed", "false");
      this.toggleIcons(false);
    }
  }
  /**
   * Toggles between play and pause icons based on video state.
   *
   * Manages icon visibility to provide clear visual feedback about
   * the current playback state and available actions.
   *
   * @param {boolean} isPlaying - Boolean indicating whether to show pause icon (true) or play icon (false)
   */
  toggleIcons(isPlaying) {
    if (!this.playIcon || !this.pauseIcon)
      return;
    if (isPlaying) {
      this.playIcon.classList.add("hidden");
      this.pauseIcon.classList.remove("hidden");
    } else {
      this.playIcon.classList.remove("hidden");
      this.pauseIcon.classList.add("hidden");
    }
  }
  /**
   * Toggles video playback between play and pause states.
   *
   * Handles the core video control logic and synchronizes the UI state
   * with the video's actual playback state. Uses the native HTML5 video
   * API for reliable cross-browser compatibility.
   *
   * @private
   */
  togglePlay() {
    if (!this.video || !this.playButton)
      return;
    if (this.video.paused) {
      this.video.play();
      this.togglePlayingState(true);
    } else {
      this.video.pause();
      this.togglePlayingState(false);
    }
  }
  /**
   * Ensures play button visibility when video enters paused state.
   *
   * Handles the video pause event to maintain UI consistency. This is
   * particularly important for programmatic pause events that don't
   * originate from user interaction.
   *
   * @private
   */
  showPlayButton() {
    this.togglePlayingState(false);
  }
  /**
   * Ensures pause button visibility when video enters playing state.
   *
   * Handles the video play event to maintain UI consistency for
   * programmatic play events.
   *
   * @private
   */
  showPauseButton() {
    this.togglePlayingState(true);
  }
};
if (!window.customElements.get("video-player")) {
  window.customElements.define("video-player", VideoPlayer);
}
